import type { SongLanguage } from "./songLanguage";

/** What the repertoire and the schedule's song picker narrow the songs to. */
export interface SongFilters {
  /** Null for every language. */
  language: SongLanguage | null;
  /** Songs by any of these; none for every author. */
  authors: string[];
}

export const noSongFilters = (): SongFilters => ({
  language: null,
  authors: [],
});

export const hasSongFilters = (filters: SongFilters | null | undefined) =>
  !!filters && (filters.language !== null || filters.authors.length > 0);

/**
 * The people a song's `author` names. Several are written with commas
 * ("Martin Luther, N.F.S. Grundtvig"), and each is an author to filter by;
 * "&" is left alone, as it more often joins a couple ("Keith & Kristyn
 * Getty").
 */
export const songAuthors = (author: string | null | undefined): string[] =>
  String(author ?? "")
    .split(",")
    .map((name) => name.trim().replace(/\s+/g, " "))
    .filter(Boolean);

const authorKey = (name: string) => name.toLocaleLowerCase();

export interface SongAuthorCount {
  name: string;
  count: number;
}

/**
 * Every author of `songs` with how many songs they wrote, by name. Spellings
 * that differ only in case are one author, under the most common one.
 */
export function countSongAuthors(
  songs: { author: string }[],
): SongAuthorCount[] {
  const byKey = new Map<
    string,
    { count: number; spellings: Map<string, number> }
  >();
  for (const song of songs) {
    for (const name of new Set(songAuthors(song.author))) {
      const key = authorKey(name);
      const entry = byKey.get(key) ?? { count: 0, spellings: new Map() };
      entry.count++;
      entry.spellings.set(name, (entry.spellings.get(name) ?? 0) + 1);
      byKey.set(key, entry);
    }
  }
  return [...byKey.values()]
    .map(({ count, spellings }) => ({
      name: [...spellings].sort((a, b) => b[1] - a[1])[0][0],
      count,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function matchesSongFilters(
  song: { author: string; language?: SongLanguage | null },
  filters: SongFilters,
): boolean {
  if (filters.language && song.language !== filters.language) return false;
  if (filters.authors.length) {
    const wanted = new Set(filters.authors.map(authorKey));
    if (!songAuthors(song.author).some((name) => wanted.has(authorKey(name))))
      return false;
  }
  return true;
}
